<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from "vue";
import { isLoadedSymbol, mapSymbol } from "vue-maplibre-gl";
import maplibregl from "maplibre-gl";
import type { GeoJSON } from "geojson";
import {
	bboxFromRing,
	featureInPolygon,
	ringToPolygon,
	type LassoAction,
	type LassoSelectMode,
	type LassoSelectResult,
} from "@/map/lasso";

const MIN_PIXEL_SPACING = 4;
const SOURCE_ID = "lasso-draw";
const FILL_LAYER_ID = "lasso-draw-fill";
const LINE_LAYER_ID = "lasso-draw-line";

const props = withDefaults(
	defineProps<{
		active?: boolean;
		layers: string | string[];
		selectMode?: LassoSelectMode;
	}>(),
	{ active: false, selectMode: "intersects" },
);

const emit = defineEmits<{
	select: [result: LassoSelectResult];
	cancel: [];
	"update:active": [value: boolean];
}>();

const isMapLoaded = inject(isLoadedSymbol)!;
const map = inject(mapSymbol)!;

const layerIds = computed(() =>
	Array.isArray(props.layers) ? props.layers : [props.layers],
);

const points = ref<maplibregl.LngLat[]>([]);
let dragging = false;
let action: LassoAction = "reset";
let restoreCursor = "";

// ---------------------------------------------------------------------------
// Map setup: overlay source/layers + drag listeners. Handlers gate on
// props.active, so arming is just a cursor/boxZoom flip.
// ---------------------------------------------------------------------------

function initMap(m: maplibregl.Map) {
	m.addSource(SOURCE_ID, {
		type: "geojson",
		data: { type: "FeatureCollection", features: [] },
	});
	m.addLayer({
		id: FILL_LAYER_ID,
		type: "fill",
		source: SOURCE_ID,
		paint: {
			"fill-color": "#6366f1",
			"fill-opacity": 0.12,
		},
	});
	m.addLayer({
		id: LINE_LAYER_ID,
		type: "line",
		source: SOURCE_ID,
		paint: {
			"line-color": "#6366f1",
			"line-width": 2,
			"line-dasharray": [2, 2],
		},
	});

	m.on("mousedown", onMouseDown);
	m.on("mousemove", onMouseMove);
	m.on("mouseup", onMapMouseUp);
	window.addEventListener("keydown", onKeyDown);

	if (props.active) {
		arm(m);
	}
}

watch(
	isMapLoaded,
	(loaded) => {
		if (loaded && map.value) initMap(map.value);
	},
	{ immediate: true },
);

// ---------------------------------------------------------------------------
// Arming / disarming
// ---------------------------------------------------------------------------

function arm(m: maplibregl.Map) {
	restoreCursor = m.getCanvas().style.cursor;
	m.getCanvas().style.cursor = "crosshair";
	// Any drag while armed is a lasso, so panning must be off before the
	// drag starts — disabling inside mousedown is too late (MapLibre's DOM
	// handler starts the pan before the map-level event reaches us).
	m.dragPan.disable();
	// Shift+drag is the "append" modifier; keep it out of MapLibre's boxZoom.
	m.boxZoom.disable();
}

function disarm(m: maplibregl.Map) {
	m.getCanvas().style.cursor = restoreCursor;
	m.dragPan.enable();
	m.boxZoom.enable();
	cancelDrag();
}

watch(
	() => props.active,
	(active) => {
		const m = map.value;
		if (!m) return;
		if (active) arm(m);
		else disarm(m);
	},
);

// ---------------------------------------------------------------------------
// Drag interaction
// ---------------------------------------------------------------------------

function onMouseDown(e: maplibregl.MapMouseEvent) {
	if (!props.active || e.originalEvent.button !== 0) return;
	dragging = true;
	action = e.originalEvent.shiftKey
		? "append"
		: e.originalEvent.altKey
			? "subtract"
			: "reset";
	points.value = [e.lngLat];
	window.addEventListener("mouseup", onWindowMouseUp);
	updateOverlay();
}

function onMouseMove(e: maplibregl.MapMouseEvent) {
	if (!dragging || !map.value) return;
	const last = points.value[points.value.length - 1];
	const prev = map.value.project(last);
	if (prev.dist(map.value.project(e.lngLat)) >= MIN_PIXEL_SPACING) {
		points.value.push(e.lngLat);
		updateOverlay();
	}
}

function onMapMouseUp() {
	if (dragging) finish();
}

function onWindowMouseUp() {
	// Release outside the canvas still ends the lasso.
	if (dragging) finish();
}

function onKeyDown(e: KeyboardEvent) {
	if (!props.active || e.key !== "Escape") return;
	cancelDrag();
	emit("cancel");
}

function cancelDrag() {
	if (dragging) {
		dragging = false;
	}
	window.removeEventListener("mouseup", onWindowMouseUp);
	points.value = [];
	clearOverlay();
}

function finish() {
	window.removeEventListener("mouseup", onWindowMouseUp);
	dragging = false;

	const pts = points.value;
	points.value = [];
	clearOverlay();
	if (pts.length < 3) return;

	const polygon = ringToPolygon(pts);
	if (!polygon) return;

	const features = queryFeatures(polygon);
	emit("select", { polygon, features, action });
}

// ---------------------------------------------------------------------------
// Overlay rendering + feature query
// ---------------------------------------------------------------------------

/** Render the in-progress stroke (line) plus closed fill once 3+ points. */
function updateOverlay() {
	const m = map.value;
	const src = m?.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
	if (!src) return;

	const line: GeoJSON.Feature = {
		type: "Feature",
		geometry: {
			type: "LineString",
			coordinates: points.value.map((p): [number, number] => [p.lng, p.lat]),
		},
		properties: {},
	};
	const features: GeoJSON.Feature[] = [line];
	const polygon = ringToPolygon(points.value);
	if (polygon) {
		features.push({ type: "Feature", geometry: polygon, properties: {} });
	}
	src.setData({ type: "FeatureCollection", features });
}

function clearOverlay() {
	const m = map.value;
	const src = m?.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
	src?.setData({ type: "FeatureCollection", features: [] });
}

function queryFeatures(polygon: GeoJSON.Polygon): GeoJSON.Feature[] {
	const m = map.value;
	if (!m) return [];
	const bbox = bboxFromRing(m, polygon.coordinates[0] as [number, number][]);
	const rendered = m.queryRenderedFeatures(bbox, { layers: layerIds.value });
	return rendered.filter((f) => featureInPolygon(polygon, f, props.selectMode));
}

onBeforeUnmount(() => {
	const m = map.value;
	window.removeEventListener("keydown", onKeyDown);
	window.removeEventListener("mouseup", onWindowMouseUp);
	if (!m) return;
	m.off("mousedown", onMouseDown);
	m.off("mousemove", onMouseMove);
	m.off("mouseup", onMapMouseUp);
	m.getCanvas().style.cursor = restoreCursor;
	if (props.active) m.dragPan.enable();
	m.boxZoom.enable();
	if (m.getLayer(FILL_LAYER_ID)) m.removeLayer(FILL_LAYER_ID);
	if (m.getLayer(LINE_LAYER_ID)) m.removeLayer(LINE_LAYER_ID);
	if (m.getSource(SOURCE_ID)) m.removeSource(SOURCE_ID);
});
</script>

<template>
	<slot />
</template>
